import axios from 'axios';
import { API_BASE_URL } from 'core/api/endpoints';
import { captureRevision } from 'core/api/revisionStore';
import { notifySessionExpired } from 'core/api/sessionEvents';
import { unwrapEnvelope } from 'core/api/unwrap';
import { ApiError } from 'core/errors/ApiError';
import { clearAdminSession, getAccessToken } from 'auth/tokenStore';
import { getLocale } from 'core/i18n/localeStore';

const WORKBOOK_TIMEOUT_MS = 120000;

function isPublicAuthRequest(config = {}) {
  if (config.skipAuth) {
    return true;
  }
  const url = String(config.url || '');
  return url.includes('/admin/login') || url.includes('/admin/refresh');
}

function shouldRefreshToken(config = {}) {
  return !config.skipTokenRefresh && !isPublicAuthRequest(config);
}

function shouldClearSessionOn401(config = {}) {
  if (config.skipSessionExpire) {
    return false;
  }
  if (isPublicAuthRequest(config)) {
    return false;
  }
  return authorizationWasAttached(config) || Boolean(config._adminRetry);
}

function readHeader(headers, name) {
  if (!headers) {
    return undefined;
  }
  if (typeof headers.get === 'function') {
    return headers.get(name) || headers.get(name.toLowerCase()) || undefined;
  }
  return headers[name] || headers[name.toLowerCase()] || headers[name.toUpperCase()];
}

function authorizationWasAttached(config = {}) {
  const headers = config.headers || {};
  return Boolean(readHeader(headers, 'Authorization'));
}

function unsetJsonContentType(headers) {
  if (!headers) {
    return;
  }
  if (typeof headers.delete === 'function') {
    headers.delete('Content-Type');
    headers.delete('content-type');
    return;
  }
  delete headers['Content-Type'];
  delete headers['content-type'];
}

function setHeader(headers, name, value) {
  if (!headers) {
    return;
  }
  if (typeof headers.set === 'function') {
    headers.set(name, value);
    return;
  }
  headers[name] = value;
}

function isFormDataBody(data) {
  return typeof FormData !== 'undefined' && data instanceof FormData;
}

function shouldSetJsonContentType(config = {}) {
  const data = config.data;
  if (data == null || data === '') {
    return false;
  }
  if (isFormDataBody(data)) {
    return false;
  }
  if (typeof Blob !== 'undefined' && data instanceof Blob) {
    return false;
  }
  if (typeof ArrayBuffer !== 'undefined' && data instanceof ArrayBuffer) {
    return false;
  }
  const existing = readHeader(config.headers, 'Content-Type');
  if (existing && String(existing).toLowerCase().includes('multipart/form-data')) {
    return false;
  }
  return typeof data === 'object' || typeof data === 'string';
}

function captureRevisionFromData(data) {
  captureRevision(data);
}

async function readBlobJson(blob) {
  if (typeof Blob === 'undefined' || !(blob instanceof Blob)) {
    return null;
  }
  try {
    const text = await blob.text();
    if (!text) {
      return {};
    }
    return JSON.parse(text);
  } catch {
    return null;
  }
}

function toApiError(error) {
  if (error instanceof ApiError) {
    return error;
  }

  if (!error.response) {
    return new ApiError({
      status: 0,
      message: error.message || 'Network error',
      isNetworkError: true,
    });
  }

  const { status, data } = error.response;
  const payload = data && typeof data === 'object' ? data : {};
  const errors = payload.errors && typeof payload.errors === 'object' ? payload.errors : {};
  const message = payload.message || error.message || 'Request failed';
  const code = errors.generation_error_code || payload.code || null;

  if (status === 409) {
    captureRevision(errors);
    if (payload.data && typeof payload.data === 'object') {
      captureRevision(payload.data);
    }
  }

  return new ApiError({
    status,
    message,
    errors,
    code,
    payload,
  });
}

async function retryWithRefreshedToken(client, config) {
  const token = getAccessToken();
  config.headers = config.headers || {};
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  } else {
    unsetAuthorizationHeader(config.headers);
  }
  return client.request(config);
}

function unsetAuthorizationHeader(headers) {
  if (!headers) {
    return;
  }
  if (typeof headers.delete === 'function') {
    headers.delete('Authorization');
    headers.delete('authorization');
    return;
  }
  delete headers.Authorization;
  delete headers.authorization;
}

async function handleUnauthorized(client, error) {
  const config = error.config || error.response?.config || {};

  if (!shouldRefreshToken(config)) {
    if (shouldClearSessionOn401(config)) {
      clearAdminSession();
      notifySessionExpired();
    }
    return Promise.reject(toApiError(error));
  }

  if (!config._adminRetry) {
    config._adminRetry = true;
    try {
      const { refreshAccessToken, waitForInflightRefresh } = await import('auth/sessionRefresh');
      await waitForInflightRefresh();
      const refreshed = await refreshAccessToken({ force: true });
      if (refreshed && getAccessToken()) {
        return retryWithRefreshedToken(client, config);
      }
    } catch {
      return Promise.reject(toApiError(error));
    }
  }

  if (shouldClearSessionOn401(config)) {
    clearAdminSession();
    notifySessionExpired();
  }

  return Promise.reject(toApiError(error));
}

export function createApiClient(overrides = {}) {
  const client = axios.create({
    baseURL: overrides.baseURL || API_BASE_URL,
    timeout: overrides.timeout || 30000,
    ...(overrides.adapter ? { adapter: overrides.adapter } : {}),
    headers: {
      Accept: 'application/json',
    },
  });

  client.interceptors.request.use(async (config) => {
    if (shouldRefreshToken(config)) {
      const { refreshAccessTokenIfNeeded } = await import('auth/sessionRefresh');
      try {
        await refreshAccessTokenIfNeeded();
      } catch {
        // Let the request proceed; a 401 will trigger the refresh retry flow.
      }
    }

    const token = (overrides.getAccessToken || getAccessToken)();
    const locale = (overrides.getLocale || getLocale)();
    config.headers = config.headers || {};
    if (token && !isPublicAuthRequest(config)) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    if (locale) {
      config.headers['X-Language'] = locale;
    }
    if (isFormDataBody(config.data)) {
      unsetJsonContentType(config.headers);
    } else if (shouldSetJsonContentType(config)) {
      setHeader(config.headers, 'Content-Type', 'application/json');
    }
    return config;
  });

  client.interceptors.response.use(
    (response) => {
      if (response.config?.responseType === 'blob') {
        return response;
      }
      const data = unwrapEnvelope(response.data);
      captureRevisionFromData(data);
      response.data = data;
      return response;
    },
    async (error) => {
      const data = error.response?.data;
      if (typeof Blob !== 'undefined' && data instanceof Blob) {
        const parsed = await readBlobJson(data);
        if (parsed && typeof parsed === 'object') {
          error.response.data = parsed;
        } else {
          error.response.data = { message: error.message || 'Request failed', errors: {} };
        }
      }

      if (error.response?.status === 401) {
        return handleUnauthorized(client, error);
      }

      return Promise.reject(toApiError(error));
    },
  );

  return client;
}

export const apiClient = createApiClient();

export async function apiGet(url, config) {
  const response = await apiClient.get(url, config);
  return response.data;
}

export async function apiPost(url, body, config) {
  const response = await apiClient.post(url, body, config);
  return response.data;
}

export async function apiPatch(url, body, config) {
  const response = await apiClient.patch(url, body, config);
  return response.data;
}

export async function apiPut(url, body, config) {
  const response = await apiClient.put(url, body, config);
  return response.data;
}

export async function apiDelete(url, config) {
  const response = await apiClient.delete(url, config);
  return response.data;
}

export function readContentDispositionHeader(headers) {
  return readHeader(headers, 'content-disposition');
}

export async function apiDownload(url, config = {}) {
  const response = await apiClient.get(url, {
    ...config,
    responseType: 'blob',
    timeout: config.timeout || WORKBOOK_TIMEOUT_MS,
  });
  let blob = response.data;
  if (typeof Blob !== 'undefined' && !(blob instanceof Blob)) {
    blob = new Blob([blob]);
  }
  return {
    blob,
    filenameHeader: readContentDispositionHeader(response.headers),
    headers: response.headers,
  };
}

export async function apiUpload(url, body, config = {}) {
  if (!isFormDataBody(body)) {
    throw new ApiError({
      status: 0,
      message: 'apiUpload requires a FormData body',
    });
  }
  const headers = { ...(config.headers || {}), Accept: 'application/json' };
  unsetJsonContentType(headers);
  const response = await apiClient.post(url, body, {
    ...config,
    headers,
    timeout: config.timeout || WORKBOOK_TIMEOUT_MS,
  });
  return response.data;
}

export { WORKBOOK_TIMEOUT_MS, setHeader, shouldSetJsonContentType, isFormDataBody };
